<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { NEmpty, NSelect, NSpin } from 'naive-ui'
import {
  useProductOwnerRunsStore,
  type ProductOwnerReviewAggregateRow,
  type ProductOwnerReviewCostPercentiles,
} from '../stores/product_owner_runs'
import { formatCents } from '../composables/formatCents'
import ProductOwnerReviewRepoTable from './ProductOwnerReviewRepoTable.vue'
import ProductOwnerReviewDailyCharts from './ProductOwnerReviewDailyCharts.vue'
import ProductOwnerReviewCostDistribution from './ProductOwnerReviewCostDistribution.vue'

// Review tab (#1183 slice 1: headline tiles; #1192 slice 2: per-repo table,
// daily charts, cost distribution) — a selectable-window view answering
// "what did this cost, and what did it buy?" — split out of
// AdminProductOwnerRuns.vue (already near the 300-line limit) into its own
// component, per #1183's explicit ask. The three #1192 sections are further
// split into their own components (table/charts/distribution) rather than
// grown inline here, same reasoning.
//
// Unlike ProductOwnerActivityHeatmap.vue — purely presentational, leaving
// loading/error/empty to its parent — this component owns its own fetch and
// every UI state end to end. The window selector lives here, so the parent
// has no reason to know about `days` at all; it only mounts this tab. That
// also keeps AdminProductOwnerRuns.spec.ts (already over the file's own
// line budget) from growing a third loading/error/empty block to test.
const store = useProductOwnerRunsStore()

const WINDOW_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
]
const days = ref(7)

async function load(): Promise<void> {
  try {
    await store.fetchReview(days.value)
  } catch {
    /* reviewLoadError surfaces in the template */
  }
}

onMounted(load)
watch(days, load)

// "Nothing ran" (empty state) is distinct from "ran, but every rate is
// zero" — computed once here rather than repeated per v-if branch so the
// template's type-checking never has to narrow `reviewTotals` itself.
const isEmpty = computed(() => !store.reviewTotals || store.reviewTotals.runs === 0)

function formatPercentage(rate: number | null | undefined): string {
  return rate == null ? '—' : `${rate.toFixed(1)}%`
}

// Fallbacks only for TypeScript's benefit — `isEmpty` already guards the
// template section that passes these to child components, so these
// defaults are never actually rendered.
const EMPTY_AGGREGATE_ROW: ProductOwnerReviewAggregateRow = {
  runs: 0,
  spend_cents: 0,
  avg_run_cents: null,
  max_run_cents: null,
  merged_pr_count: 0,
  cost_per_merged_pr_cents: null,
  idle_rate: null,
}
const EMPTY_COST_PERCENTILES: ProductOwnerReviewCostPercentiles = {
  p50: null,
  p75: null,
  p90: null,
  p95: null,
  p99: null,
  max: null,
  top_5_percent_share: null,
}
const allRepos = computed(() => store.reviewAllRepos ?? EMPTY_AGGREGATE_ROW)
const costPercentiles = computed(() => store.reviewCostPercentiles ?? EMPTY_COST_PERCENTILES)

const runsLabel = computed(() => store.reviewTotals?.runs ?? 0)
const totalSpendLabel = computed(() => formatCents(store.reviewTotals?.total_spend_cents))
const mergedPrCountLabel = computed(() => store.reviewTotals?.merged_pr_count ?? 0)
const costPerMergedPrLabel = computed(() => formatCents(store.reviewTotals?.cost_per_merged_pr_cents))
const failureRateLabel = computed(() => formatPercentage(store.reviewTotals?.failure_rate))
const idleRateLabel = computed(() => formatPercentage(store.reviewTotals?.idle_rate))
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="product-owner-review-tab">
    <label class="flex flex-col gap-1 text-xs text-white/60 max-w-xs">
      Window
      <NSelect v-model:value="days" :options="WINDOW_OPTIONS" data-testid="product-owner-review-window" />
    </label>

    <div v-if="store.reviewLoading && !store.reviewLoaded" class="flex items-center justify-center py-12">
      <NSpin />
    </div>

    <div v-else-if="store.reviewLoadError" class="text-red-300 text-sm" data-testid="product-owner-review-load-error">
      Failed to load review: {{ store.reviewLoadError }}
    </div>

    <NEmpty
      v-else-if="isEmpty"
      description="No runs in this window."
      class="my-12"
      data-testid="product-owner-review-empty"
    />

    <div v-else class="flex flex-col gap-6">
      <dl class="grid grid-cols-2 sm:grid-cols-3 gap-3 m-0" data-testid="product-owner-review-tiles">
        <div
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-1"
          data-testid="product-owner-review-tile-runs"
        >
          <dt class="text-xs text-white/40">Runs</dt>
          <dd class="m-0 text-2xl font-semibold text-white tabular-nums">{{ runsLabel }}</dd>
        </div>
        <div
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-1"
          data-testid="product-owner-review-tile-spend"
        >
          <dt class="text-xs text-white/40">Total spend</dt>
          <dd class="m-0 text-2xl font-semibold text-white tabular-nums">{{ totalSpendLabel }}</dd>
        </div>
        <div
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-1"
          data-testid="product-owner-review-tile-merged"
        >
          <dt class="text-xs text-white/40">PRs merged</dt>
          <dd class="m-0 text-2xl font-semibold text-white tabular-nums">{{ mergedPrCountLabel }}</dd>
        </div>
        <div
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-1"
          data-testid="product-owner-review-tile-cost-per-pr"
        >
          <dt class="text-xs text-white/40">Cost per merged PR</dt>
          <dd class="m-0 text-2xl font-semibold text-white tabular-nums">{{ costPerMergedPrLabel }}</dd>
          <p class="m-0 text-[11px] text-white/30">
            Throughput, not value — many merges are the agent's own self-filed work.
          </p>
        </div>
        <div
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-1"
          data-testid="product-owner-review-tile-failure-rate"
        >
          <dt class="text-xs text-white/40">Failure rate</dt>
          <dd class="m-0 text-2xl font-semibold text-white tabular-nums">{{ failureRateLabel }}</dd>
        </div>
        <div
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-1"
          data-testid="product-owner-review-tile-idle-rate"
        >
          <dt class="text-xs text-white/40">Idle rate</dt>
          <dd class="m-0 text-2xl font-semibold text-white tabular-nums">{{ idleRateLabel }}</dd>
        </div>
      </dl>

      <div class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-white/80 m-0">Per repo</h3>
        <ProductOwnerReviewRepoTable :per-repo="store.reviewPerRepo" :all-repos="allRepos" />
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-white/80 m-0">Daily</h3>
        <ProductOwnerReviewDailyCharts :days="store.reviewPerDay" />
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-white/80 m-0">Cost distribution</h3>
        <ProductOwnerReviewCostDistribution :cost-percentiles="costPercentiles" />
      </div>
    </div>
  </div>
</template>
